import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, Link } from 'react-router-dom'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Card } from '../components/ui/Card'
import { useAuth } from '../lib/auth/AuthContext'
import { t } from '../lib/i18n'
import { apiFetch } from '../lib/api-client'

const strings = t()

const signupSchema = z.object({
  email: z.string().min(1, strings.auth.required).email(strings.auth.invalidEmail),
  password: z.string().min(8, strings.auth.passwordMin8),
  name: z.string().min(2, strings.auth.nameMin),
  company_name: z.string().min(2, strings.auth.companyMin),
})

type SignupForm = z.infer<typeof signupSchema>

export function SignupPage() {
  const { user, isLoading } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (!isLoading && user) {
      navigate('/dashboard', { replace: true })
    }
  }, [user, isLoading, navigate])

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<SignupForm>({
    resolver: zodResolver(signupSchema),
  })

  const onSubmit = async (data: SignupForm) => {
    try {
      const result = await apiFetch<{ access_token: string; refresh_token: string }>(
        '/api/v1/auth/signup',
        { method: 'POST', body: JSON.stringify(data) },
      )
      localStorage.setItem('boligflow_access_token', result.access_token)
      localStorage.setItem('boligflow_refresh_token', result.refresh_token)
      navigate('/dashboard', { replace: true })
    } catch (err) {
      setError('root', { message: err instanceof Error ? err.message : strings.auth.signupError })
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">{strings.general.loading}</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">{strings.brand.name}</h1>
          <p className="text-sm text-gray-500 mt-1">{strings.auth.signupSubtitle}</p>
        </div>

        <Card>
          <h2 className="text-lg font-semibold text-gray-900 mb-6">{strings.auth.signup}</h2>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
            <Input
              label={strings.auth.email}
              type="email"
              autoComplete="email"
              placeholder={strings.auth.emailPlaceholder}
              error={errors.email?.message}
              {...register('email')}
            />

            <Input
              label={strings.auth.nameLabel}
              type="text"
              autoComplete="name"
              placeholder={strings.auth.namePlaceholder}
              error={errors.name?.message}
              {...register('name')}
            />

            <Input
              label={strings.auth.companyLabel}
              type="text"
              placeholder={strings.auth.companyPlaceholder}
              error={errors.company_name?.message}
              {...register('company_name')}
            />

            <Input
              label={strings.auth.password}
              type="password"
              autoComplete="new-password"
              placeholder={strings.auth.passwordPlaceholder}
              error={errors.password?.message}
              {...register('password')}
            />

            {errors.root && (
              <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
                {errors.root.message}
              </p>
            )}

            <Button
              type="submit"
              className="w-full"
              isLoading={isSubmitting}
            >
              {isSubmitting ? strings.auth.signingUp : strings.auth.signup}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              {strings.auth.alreadyAccount}{' '}
              <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
                {strings.auth.loginLink}
              </Link>
            </p>
          </div>
        </Card>
      </div>
    </div>
  )
}
